import { Dispatch } from 'react';

import { AuthUserType } from '../redux/workBackendSlice';
import { initLocalDB, initializeSlices, saveAllDb, isDbKeyType, SignUpPayload, signUpAllDb } from '../redux/dataBackend';
import { ArrowDb } from '../redux/arrowDbSlice';
import { addRelationUserImage } from '../redux/relationUserImageDbSlice';
import { addRelationAlbumImage } from '../redux/relationAlbumImageDbSlice';
import { updateLatelyImage } from '../redux/latelyImageDbSlice';
import { generateUserId, addUser, setCurrentUser, signUpUser } from '../redux/userDbSlice';
import { generateFolderId, addFolder, setCurrentFolder } from '../redux/folderDbSlice';
import { generateBookId, addBook, setCurrentBook } from '../redux/bookDbSlice';
import { generateSheetId, addSheet, setDefaultSheet } from '../redux/sheetDbSlice';
import { generateSheetInformationId, addSheetInformation } from '../redux/sheetInformationDbSlice';
import { defaultPaletteName, generatePaletteId, addPalette } from '../redux/paletteDbSlice';
import { generateCardTemplateId, addCardTemplate } from '../redux/cardTemplateDbSlice';
import { generateCardTemplateAttributeId, addCardTemplateAttribute } from '../redux/cardTemplateAttributeDbSlice';
import { generateAlbumId, addAlbum } from '../redux/albumDbSlice';
import { generateSheetTreeId, addSheetTree } from '../redux/sheetTreeDbSlice';
import { generateRelationUserFolderId, addRelationUserFolder } from '../redux/relationUserFolderDbSlice';
import { generateRelationUserBookId, addRelationUserBook } from '../redux/relationUserBookDbSlice';
import { generateRelationUserPaletteId, addRelationUserPalette} from '../redux/relationUserPaletteDbSlice';
import { generateRelationUserCardTemplateId, addRelationUserCardTemplate } from '../redux/relationUserCardTemplateDbSlice';
import { generateRelationFolderBookId, addRelationFolderBook } from '../redux/relationFolderBookDbSlice';
import { generateRelationPaletteCardTemplateId, addRelationPaletteCardTemplate } from '../redux/relationPaletteCardTemplateDbSlice';
import { generateRelationUserAlbumId, addRelationUserAlbum } from '../redux/relationUserAlbumDbSlice';
import { generateLatelyCardTemplateId, addLatelyCardTemplate } from '../redux/latelyCardTemplateDbSlice';
import { generateLatelyImageId, addLatelyImage } from '../redux/latelyImageDbSlice';
import { addImage } from '../redux/imageDbSlice';
import { generateUserConditionId, addUserCondition } from '../redux/userConditionSlice';
import { FontType, defaultFrame } from '../redux/cardAttributeModelIndex';

import { initial } from '../card_components/index';

/* types ------------------------------------------------------------------------------------------ */

export type CoordinateType = { x: number; y: number; }

export type FullSizeImageAddType = { id: string; width: number; height: number; }

export type Hash<T> = { [key in string]: T; }

export type HashLoop<T> = { [key in string]: Hash<T>; };

/* variables ------------------------------------------------------------------------------------------ */

/* functions ------------------------------------------------------------------------------------------ */

export const deepCopy = <T, _>(array: T): T => {// generics 1つではエラーになるので、_はダミーとして
  return JSON.parse(JSON.stringify(array));
}

export const toSnakeCase = (str: string) => {
  return str.split(/(?=[A-Z])/).join('_').toLowerCase();
}

export const toCamelCase = (str: string) => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

export const isSameObject = (a: Object, b: Object): boolean => {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const isCorrectKey = <T extends Object, Key extends keyof T>(value: T, word: unknown): word is Key => {
  return typeof word === 'string' && value && typeof value === 'object' && !Array.isArray(value) && word in value;
}

export const isSameType = <T, U extends T>(a: T, b: unknown): b is U => {
  if (a === null && b === null) return true;
  else if (a === null || b === null) return false;
  else if (typeof a === 'object' && typeof b === 'object') {
    if (Array.isArray(a) && Array.isArray(b)) return true;
    else if (Array.isArray(a) || Array.isArray(b)) return false;
    else if (Object.keys(a).length === 0 || Object.keys(b).length === 0) return true;
    else {
      const aEntries = Object.entries(a);
      const aKeys = aEntries.map(entry => entry[0]);
      const aProps = aEntries.map(entry => entry[1]);
      const bEntries = Object.entries(b);
      const bKeys = bEntries.map(entry => entry[0]);
      const bProps = bEntries.map(entry => entry[1]);

      if (aKeys.length !== bKeys.length) return false;

      let wrong = false;
      aKeys.forEach((key, ix) => {
        const prop = aProps[ix];
        const bTargetIx = bKeys.indexOf(key);
        if (bTargetIx === -1) wrong = true;
        else if (!isSameType(bProps[bTargetIx], prop)) wrong = true;
      })
      if (wrong) return false;

      return true;
    }
  } else return typeof a === typeof b;
}

export const getTimestamp = () => {
  const date = new Date();
  const dateTime: string = date.getFullYear() + '-' +
  ('00' + (date.getMonth() + 1)).slice(-2) + '-' +
  ('00' + (date.getDate())).slice(-2) + ' ' +
  date.getHours() + ':' + date.getMinutes() + ':' + date.getSeconds();
  return dateTime;
}

export const getImgSize = (dataUrl: string) => {
  const img = new Image();
  img.src = dataUrl;
  img.style.display = 'none';
  const width = img.width;
  const height = img.height;
  img.remove();

  return [width, height];
}

export const getFontData = (font: FontType): FontType => {
  const value = deepCopy(font);
  if (value.direction === 'vertical-rl') {
    type YokoType = 'left' | 'center' | 'right';
    type TateType = 'start' | 'center' | 'end';
    type YokoHashType = { [key in TateType]: YokoType };
    type TateHashType = { [key in YokoType]: TateType };
    const yokoHash: YokoHashType = {'start': 'left', 'center': 'center', 'end': 'right'};
    const tateHash: TateHashType = {'left': 'end', 'center': 'center', 'right': 'start'};
    const a = value.yoko;
    const b = value.tate;
    value.yoko = yokoHash[b];
    value.tate = tateHash[a];
  }
  return value;
}

export const getArrowStrokeWidth = (arrow?: ArrowDb) => {
  const lineStyleEffect = (arrow?.lineStyle === 'double') ? 0.5 : 1;
  if (arrow?.thickness === 'verythin') return 1 * lineStyleEffect;
  else if (arrow?.thickness === 'thin') return 2 * lineStyleEffect;
  else if (arrow?.thickness === 'medium') return 3 * lineStyleEffect;
  else if (arrow?.thickness === 'thick') return 5 * lineStyleEffect;
  else if (arrow?.thickness === 'verythick') return 7 * lineStyleEffect;
  else return 3;
}

export const getArrowDasharray = (arrow?: ArrowDb) => {
  if (arrow?.lineStyle === 'dotted') return getArrowStrokeWidth(arrow);
  else if (arrow?.lineStyle === 'dashed') return (getArrowStrokeWidth(arrow) * 5) + ' ' + getArrowStrokeWidth(arrow);
  else return 'none';
}

export const makeLately = (latelyIds: string[], targetId: string, method: 'add' | 'delete'): string[] => {
  const newLatelyIds = deepCopy(latelyIds);
  const existIx = newLatelyIds.indexOf(targetId);
  if (existIx >= 0) newLatelyIds.splice(existIx, 1);
  if (method === 'add') {
    const latelyIdsLength = newLatelyIds.unshift(targetId);
    if (latelyIdsLength > 20) newLatelyIds.splice(latelyIdsLength - 1, 1);
  }
  return newLatelyIds;
}

type UpdateImageDbsProps = {
  dispatch: Dispatch<any>;
  imageId: string;
  dataUrlBase64: string;
  userId: string | undefined;
  albumId?: string;
  imagesLength?: number;
  latelyIds: string[];
  latelyImagesId: string;
}
export const updateImageDbs = (props: UpdateImageDbsProps) => {
  const { dispatch, imageId, dataUrlBase64, userId, albumId, imagesLength, latelyIds, latelyImagesId } = props;
  dispatch(addImage({id: imageId, authorId: userId || 'no user', dataUrl: dataUrlBase64}));
  // add relation between user and image
  dispatch(addRelationUserImage({userId: userId || 'no user', imageId: imageId}));
  // add relation between album and image
  albumId && imagesLength && dispatch(addRelationAlbumImage({albumId: albumId, imageId: imageId, order: imagesLength}));
  // update lately images
  const newLatelyIds = makeLately(latelyIds, imageId, 'add');
  dispatch(updateLatelyImage({id: latelyImagesId, imageIds: newLatelyIds}));
}

const isCorrectHash = <T extends Object, _>(a: T | T[], b: Object): b is { [key in string]: T } => {
  return Boolean(b) && Object.keys(b).length !== 0;
}
export const updateHashProps = <T extends Object>(initial: T | T[], values: Object): [boolean, { [key in string]: T }] => {
  let changed = false;
  if (!isCorrectHash(initial, values)) return [changed, {}];

  const checkProps = (id: string, value: T, props: [string, any][], propKeys: string[]): T => {
    // check loaded data having all props, or correct type props
    const newValue = deepCopy(value);
    const valueProps = Object.entries(value);
    props.forEach(prop => {
      const propKey = prop[0];
      const propValue = prop[1];
      const target = valueProps.find(valueProp => valueProp[0] === propKey)
      if (!target) {
        if (propValue === undefined) return;
        console.log(`#### No "${propKey}" prop in ${id}`);
        changed = true;
      } else if (propValue === undefined) {
        return;
      } else if (!isSameType(propValue, target[1])) {
        console.log(`#### "${propKey}" prop's type is not ${typeof propValue === 'object' ? 'correct object' : typeof propValue} in ${id}`);
        changed = true;
        if (!isCorrectKey(newValue, propKey)) return;
        if (typeof propValue === 'object' && !Array.isArray(propValue)) {
          if ('display' in propValue && typeof propValue.display === 'boolean' && 'cardAttributeModel' in newValue && target[0] === 'frame' && typeof target[1] === 'boolean') newValue[propKey] = { ...propValue, display: target[1] };
          else if (typeof target[1] === 'object' && !Array.isArray(target[1])) {
            const keys = Object.keys(target[1]).map(key => key in propValue && isSameType(target[1][key], propValue[key]) ? key : undefined);
            const succeededProps: [string, any][] = [];
            keys.forEach(key => key ? succeededProps.push([key, target[1][key]]) : undefined)
            newValue[propKey] = { ...propValue, ...Object.fromEntries(succeededProps) };
          } else newValue[propKey] = propValue;
        } else newValue[propKey] = propValue;
      }
    })
    // check loaded data not having needless props
    const valueKeys = Object.keys(value);
    valueKeys.forEach(prop => {
      if (!propKeys.includes(prop)) {
        console.log(`#### Needless "${prop}" prop in ${id}`);
        if (isCorrectKey(newValue, prop)) {
          delete newValue[prop];
          changed = true;
        }
      }
    })
    return newValue;
  }

  if (Array.isArray(initial)) {
    if (Object.keys(values).length === 0) return [changed, {}];
    const updatedValues: { [key in string]: T } = {};
    const propsArray: [string, any][][] = [];
    initial.forEach((initialValue, ix) => {
      propsArray[ix] = Object.entries(initialValue);
    })

    Object.keys(values).forEach(id => {
      const value = values[id];
      // get corresponding initial
      const valueProps = Object.entries(value);
      const valueKeys = valueProps.map(prop => prop[0]);
      let keyProp = '';
      if (value.hasOwnProperty('sheetKind')) keyProp = 'sheetKind';
      else if (!value.hasOwnProperty('sheetKind') && value.hasOwnProperty('kind')) keyProp = 'kind';
      else if (value.hasOwnProperty('type')) keyProp = 'type';
      else if (value.hasOwnProperty('cardAttributeModel')) keyProp = 'cardAttributeModel';
      const keyContent = valueProps[valueKeys.findIndex(key => key === keyProp)][1];
      const targetIx = propsArray.findIndex(props => {
        const isTarget = props.findIndex(prop => prop[0] === keyProp && prop[1] === keyContent) >= 0;
        return isTarget;
      })
      const thisInitial = initial[targetIx];
      const props = Object.entries(thisInitial);
      const propKeys = props.map(prop => prop[0]);
      const newValue = checkProps(id, value, props, propKeys);
      updatedValues[id] = { ...thisInitial, ...newValue };
    })
    return [changed, updatedValues];
  } else {
    const updatedValues: { [key in string]: T } = {};
    const props = Object.entries(initial);
    const propKeys = props.map(prop => prop[0]);
    Object.keys(values).forEach(id => {
      const value = values[id];
      const newValue = checkProps(id, value, props, propKeys);
      updatedValues[id] = { ...initial, ...newValue };
    })
    return [changed, updatedValues];
  }
}

export type SignUpDataType = {
  newId: string;
  oldId?: string;
  mail: string;
  name: string;
  language: string;
}
export const makeInitialUserData = (dispatch: Dispatch<any>, authUser: AuthUserType, signUpData?: SignUpDataType, callback?: () => void) => {
  console.log(signUpData)
  const dateTime = getTimestamp();
  const userId = signUpData ? signUpData.newId : generateUserId();
  const mail = signUpData ? signUpData.mail : 'user1@magica.com';
  const name = signUpData ? signUpData.name : 'user1';
  const language = signUpData ? signUpData.language : 'ja';
  if (signUpData && signUpData.oldId) {
    console.log('signup')
    dispatch(signUpUser({ id: userId, mail, name, language, registrationDateTime: dateTime }));
    dispatch(signUpAllDb({ newId: userId, oldId: signUpData.oldId }, callback));
    dispatch(setCurrentUser(userId));
  } else {
    console.log('make new user')
    dispatch(initializeSlices());
    // make default user
    dispatch(addUser({id: userId, mail, name, language, registrationDateTime: dateTime}));
    dispatch(setCurrentUser(userId));
    // make default folder
    const folderId = generateFolderId();
    dispatch(addFolder({id: folderId, name: '新しいフォルダ'}));
    dispatch(setCurrentFolder(folderId));
    // make relation between default user and default folder
    const relationUserFolderId = generateRelationUserFolderId();
    dispatch(addRelationUserFolder({id: relationUserFolderId, userId: userId, folderId: folderId, order: 0}));
    // make default book
    const bookId = generateBookId();
    dispatch(addBook({ id: bookId, name: '新しいブック', notes: 'ブック説明' }));
    dispatch(setCurrentBook(bookId));
    // make relation between default user and default book
    const relationUserBookId = generateRelationUserBookId();
    dispatch(addRelationUserBook({id: relationUserBookId, userId: userId, bookId: bookId}));
    // make relation between default folder and default book
    const relationFolderBookId = generateRelationFolderBookId();
    dispatch(addRelationFolderBook({id: relationFolderBookId, userId: userId, folderId: folderId, bookId: bookId, order: 0}));
    // make default sheet
    const sheetId = generateSheetId();
    dispatch(addSheet({id: sheetId, name: '新しいシート', sheetKind: 'map'}));
    dispatch(setDefaultSheet(sheetId));
    // make sheet information
    const sheetInformationId = generateSheetInformationId();
    dispatch(addSheetInformation({id: sheetInformationId, sheetId: sheetId, name: 'args', content: 'あれやこれや', order: 0}));
    // make sheet tree
    const sheetTreeId = generateSheetTreeId();
    dispatch(addSheetTree({id: sheetTreeId, bookId: bookId, sheetId: sheetId, order: 0}));
    // make default palette
    const standardPaletteId = generatePaletteId();
    dispatch(addPalette({id: standardPaletteId, name: defaultPaletteName, standard: true}));
    const paletteId = generatePaletteId();
    dispatch(addPalette({id: paletteId, name: 'パレット１', standard: false}));
    // make relation between default user and default palette
    const rupId1 = generateRelationUserPaletteId();
    dispatch(addRelationUserPalette({id: rupId1, userId: userId, paletteId: standardPaletteId, order: 0}));
    const rupId2 = generateRelationUserPaletteId();
    dispatch(addRelationUserPalette({id: rupId2, userId: userId, paletteId: paletteId, order: 1}));
    // make standard card templates
    const uiCardId = generateCardTemplateId();
    dispatch(addCardTemplate({id: uiCardId, name: 'UiSketchCard', kind: 'ui', author: 'u:master', standard: true, defaultWidth: initial.cardWidth, defaultHeight: initial.cardHeight, frame: {display: true, color: '#000000'}, background: {display: true, color: '#ffffff'}, shadow: true, notes: '', isGroup: false}));
    const memoCardId = generateCardTemplateId();
    dispatch(addCardTemplate({id: memoCardId, name: 'MemoCard', kind: 'memo', author: 'u:master', standard: true, defaultWidth: initial.cardWidth, defaultHeight: initial.cardHeight, frame: {display: true, color: '#000000'}, background: {display: true, color: '#ffffff'}, shadow: true, notes: '', isGroup: false}));
    const drawCardId = generateCardTemplateId();
    dispatch(addCardTemplate({id: drawCardId, name: 'DrawCard', kind: 'draw', author: 'u:master', standard: true, defaultWidth: initial.cardWidth, defaultHeight: initial.cardHeight, frame: {display: true, color: '#000000'}, background: {display: true, color: '#ffffff'}, shadow: true, notes: '', isGroup: false}));
    // make relation between default user and standard card templates
    const ruucId = generateRelationUserCardTemplateId();
    dispatch(addRelationUserCardTemplate({id: ruucId, userId: userId, cardTemplateId: uiCardId}));
    const rumcId = generateRelationUserCardTemplateId();
    dispatch(addRelationUserCardTemplate({id: rumcId, userId: userId, cardTemplateId: memoCardId}));
    const rudcId = generateRelationUserCardTemplateId();
    dispatch(addRelationUserCardTemplate({id: rudcId, userId: userId, cardTemplateId: drawCardId}));
    const rpucId = generateRelationPaletteCardTemplateId();
    dispatch(addRelationPaletteCardTemplate({id: rpucId, paletteId: standardPaletteId, cardTemplateId: uiCardId, order: 0}));
    const rpmcId = generateRelationPaletteCardTemplateId();
    dispatch(addRelationPaletteCardTemplate({id: rpmcId, paletteId: standardPaletteId, cardTemplateId: memoCardId, order: 1}));
    const rpdcId = generateRelationPaletteCardTemplateId();
    dispatch(addRelationPaletteCardTemplate({id: rpdcId, paletteId: standardPaletteId, cardTemplateId: drawCardId, order: 2}));
    // make default album
    const albumId = generateAlbumId();
    dispatch(addAlbum({id: albumId, name: '新しいアルバム'}));
    // make relation between default user and default album
    const ruaId = generateRelationUserAlbumId();
    dispatch(addRelationUserAlbum({id: ruaId, userId: userId, albumId: albumId, order: 0}));
    // make default user's latelyCards
    const latelyCardTemplateId = generateLatelyCardTemplateId();
    dispatch(addLatelyCardTemplate({id: latelyCardTemplateId, userId: userId, cardTemplateIds: []}));
    // make default user's latelyImages
    const latelyImagesId = generateLatelyImageId();
    dispatch(addLatelyImage({id: latelyImagesId, userId: userId, imageIds: []}));
    // mage default user's condition
    const userConditionId = generateUserConditionId();
    dispatch(addUserCondition({ id: userConditionId, userId: userId, bookTreeDisplay: false, splitRatios: { 'userHome': [50, 50], 'bookEditor': [25, 75], 'cardBrowser': [25, 75], 'imageBrowser': [25, 75] }, zoomRates: {}, noteDisplay: { 'bookEditor': {}, 'cardTemplateDesign': false, 'card': false }, workNoteRatios: { 'bookEditor': {}, 'cardTemplateDesign': [75, 25], 'card': [75, 25] }, where: 'sheet', when: dateTime, folderId: folderId, bookId: bookId, sheetId: sheetId }));

    if (authUser === 'noAuth') localStorage.setItem('loginId', userId);
    dispatch(saveAllDb());
    return { userId, paletteId, folderId };
  }
}

export const rewriteUserId = <T extends { [key in string]: Object }>(values: T, payloadIds: SignUpPayload): T => {
  const newValues = deepCopy(values);
  const { newId, oldId } = payloadIds;

  const ids = Object.keys(values);
  ids.forEach(id => {
    const value = newValues[id];
    if ('userId' in value && value.userId === oldId) {
      value.userId = newId;
    } else if ('authorId' in value && value.authorId === oldId) {
      value.authorId = newId;
    } else if ('author' in value && value.author === oldId) {
      value.author = newId;
    }
  })

  return newValues;
}

export const getAuthErrorMessage = (ev: any): string => {
  // if (ev instanceof FirebaseError) {
  //   if (ev.code === 'auth/invalid-email') return '適切なメールアドレスではありません';
  //   else if (ev.code === 'auth/email-already-in-use') return 'すでに使用されているメールアドレスです';
  //   else if (ev.code === 'auth/weak-password') return 'パスワードは6文字以上にしてください';
  //   else return '入力内容に問題があります';
  // } else return '入力内容に問題があります';
  return 'error'
}

export const localSave = async (path: string[], value: Hash<Object> | Object) => {
  try {
    if (path.length === 1) {
      const [key] = path;
      if (!isDbKeyType(key)) {
        console.log('### ' + key + ' is wrong key');
        return;
      }
      const db = await initLocalDB();
      const tx = db.transaction(key, 'readwrite');
      const ids = Object.keys(value);
      for (const id of ids) {
        if (isCorrectKey(value, id)) await tx.store.put({ id, ...value[id] });
      }
      await tx.done;
    } else if (path.length === 2) {
      const [key, id] = path;
      if (!isDbKeyType(key)) {
        console.log('### ' + key + ' is wrong key');
        return;
      }
      const db = await initLocalDB();
      await db.put(key, { id, ...value });
    } else {
      console.log('path length ' + path.length + ' is invalid');
      return;
    }
  } catch (err) {
    console.log(err);
  }
}

export const localDelete = async (path: string[]) => {
  if (path.length === 2) {
    const [key, id] = path;
    if (!isDbKeyType(key)) {
      console.log('### ' + key + ' is wrong key');
      return;
    }
    const db = await initLocalDB();
    await db.delete(key, id);
  } else {
    console.log('path length ' + path.length + ' is invalid');
    return;
  }
}

export const setTokensToCookie = (data: any) => {
  console.log(data.user_id);
  console.log(data.token);
  console.log(data.expiry_date);// 有効期限
  console.log(data.refresh_token_expiry_date);// 有効期限
  let expires = new Date(data.expiry_date);
  let refresh_expires = new Date(data.refresh_token_expiry_date);

  document.cookie = "user_id="+data.user_id+"; expires=" + refresh_expires.toUTCString() + "; path=/";
  document.cookie = "token="+data.token+"; expires=" + expires.toUTCString() + "; path=/";
  document.cookie = "expiry_date="+data.expiry_date+"; expires=" + expires.toUTCString() + "; path=/";
  document.cookie = "refresh_token="+data.refresh_token+"; expires=" + refresh_expires.toUTCString() + "; path=/";
}

export const clearTokensOfCookie = () => {
  document.cookie = 'token=; max-age=0';
  document.cookie = 'refresh_token=; max-age=0';
  console.log(document.cookie);
}

export const getCookie = (name: string) => {
  let nameEQ = name + "=";
  let ca = document.cookie.split(';');
  for(let i=0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
}
